import { useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/base/MovieCard';
import { movies } from '@/mocks/movies';
import { usePageMeta } from '@/hooks/usePageMeta';

const resultTypeOptions = ['全部', '电视剧', '电影'];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const resultType = searchParams.get('type') || '全部';
  const [input, setInput] = useState(query);

  usePageMeta({
    title: query ? `搜索“${query}” - 伊人在线` : '搜索影视 - 伊人在线',
    description: query
      ? `伊人在线搜索“${query}”的结果页面，可按电视剧、电影筛选，快速找到相关影视作品。`
      : '在伊人在线搜索你想看的电视剧与电影，支持按标题关键词匹配。',
    keywords: '影视搜索,电视剧搜索,电影搜索,伊人在线',
  });

  const results = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    if (!keyword) return [];
    return movies.filter((m) => {
      const matchedTitle = m.title.toLowerCase().includes(keyword);
      const matchedActor = m.actors.some((a) => a.toLowerCase().includes(keyword));
      const matchedGenre = m.genres.some((g) => g.toLowerCase().includes(keyword));
      const matched = matchedTitle || matchedActor || matchedGenre;
      if (!matched) return false;
      if (resultType !== '全部' && m.type !== resultType) return false;
      return true;
    });
  }, [query, resultType]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams();
    if (input.trim()) next.set('q', input.trim());
    if (resultType !== '全部') next.set('type', resultType);
    setSearchParams(next);
  };

  const handleTypeChange = (type: string) => {
    const next = new URLSearchParams(searchParams);
    if (type === '全部') next.delete('type');
    else next.set('type', type);
    setSearchParams(next);
  };

  const hotKeywords = ['星河彼岸', '悬疑', '古装', '科幻', '爱情'];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16 md:pt-[72px]">
        <div className="border-b border-background-200/70 bg-background-100/60">
          <div className="mx-auto px-4 py-10 md:px-10 md:py-12">
            <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500" aria-label="面包屑">
              <Link to="/" className="cursor-pointer transition-colors hover:text-primary-700">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-800">搜索</span>
            </nav>
            <h1 className="mt-3 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
              搜索影视
            </h1>

            <form onSubmit={handleSubmit} className="mt-5 flex max-w-2xl flex-col gap-3 sm:flex-row">
              <div className="relative flex-1">
                <i className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400"></i>
                <input
                  type="text"
                  name="q"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="输入影视名称、主演或类型关键词"
                  className="w-full rounded-md border border-background-300 bg-background-50 py-3 pl-10 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </div>
              <button
                type="submit"
                className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                搜索
              </button>
            </form>

            <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
              <span className="text-foreground-500">热门搜索：</span>
              {hotKeywords.map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => {
                    setInput(k);
                    const next = new URLSearchParams();
                    next.set('q', k);
                    setSearchParams(next);
                  }}
                  className="cursor-pointer rounded-full bg-background-50 px-3 py-1 text-xs text-foreground-700 ring-1 ring-background-200 transition-colors hover:bg-accent-100 hover:text-accent-800"
                >
                  {k}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mx-auto px-4 py-8 md:px-10 md:py-10">
          {query ? (
            <>
              <div className="flex flex-wrap items-center justify-between gap-4">
                <p className="text-sm text-foreground-700">
                  搜索 <span className="font-semibold text-accent-600">“{query}”</span> 共找到{' '}
                  <span className="font-semibold text-accent-600">{results.length}</span> 个结果
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  {resultTypeOptions.map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => handleTypeChange(type)}
                      className={`cursor-pointer whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors ${
                        resultType === type
                          ? 'bg-secondary-500 text-background-50'
                          : 'bg-background-100 text-foreground-700 hover:bg-secondary-100'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              {results.length === 0 ? (
                <div className="mt-8 rounded-lg bg-background-100 py-20 text-center ring-1 ring-background-200/70">
                  <i className="ri-search-eye-line text-4xl text-foreground-300"></i>
                  <p className="mt-3 text-sm text-foreground-700">
                    没有找到相关影视，您可以尝试其他关键词
                  </p>
                  <p className="mt-1 text-xs text-foreground-500">
                    试试更换关键词，或浏览下方为你推荐的内容
                  </p>
                  <div className="mt-6 flex flex-wrap justify-center gap-3">
                    <Link
                      to="/category"
                      className="whitespace-nowrap cursor-pointer rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                    >
                      浏览全部影视
                    </Link>
                  </div>

                  <div className="mx-auto mt-10 max-w-5xl px-4 text-left">
                    <h2 className="font-heading text-base font-bold text-foreground-950">
                      为你推荐
                    </h2>
                    <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                      {[...movies]
                        .sort((a, b) => b.heat - a.heat)
                        .slice(0, 5)
                        .map((m) => (
                          <MovieCard key={m.id} movie={m} showHeat />
                        ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 md:gap-5 lg:grid-cols-5">
                  {results.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="rounded-lg bg-background-100 py-16 text-center ring-1 ring-background-200/70">
              <i className="ri-movie-2-line text-4xl text-foreground-300"></i>
              <p className="mt-3 text-sm text-foreground-700">输入关键词，开始查找你想看的影视</p>
              <p className="mt-1 text-xs text-foreground-500">支持按影视名称、主演、类型进行匹配</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}